import React, { useState } from 'react';
import { formatDate, formatTime } from './sheetDateFormat';

export default function ScheduleTable({ data, loading, error, allUniqueClasses }) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  /** 'thumbnail' | 'reminderPoster' — which image is shown in the details modal */
  const [mediaTab, setMediaTab] = useState('thumbnail');

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setMediaTab(row.thumbnail ? 'thumbnail' : 'reminderPoster');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  // Function to convert Google Drive link to direct view link
  const convertGoogleDriveLink = (url) => {
    if (!url) {
      console.log('convertGoogleDriveLink: No URL provided');
      return url;
    }
    
    console.log('convertGoogleDriveLink: Original URL:', url);
    
    // Check if it's a Google Drive link - handle different formats
    let fileId = null;
    
    // Format: https://drive.google.com/file/d/FILE_ID/view
    let match = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (match) {
      fileId = match[1];
    }
    
    // Format: https://drive.google.com/open?id=FILE_ID
    if (!fileId) {
      match = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (match) {
        fileId = match[1];
      }
    }
    
    if (fileId) {
      const convertedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
      console.log('convertGoogleDriveLink: Converted URL:', convertedUrl);
      return convertedUrl;
    }
    
    console.log('convertGoogleDriveLink: No file ID found, returning original URL');
    return url;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <div className="text-gray-600 text-sm">Loading class schedule...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col items-center justify-center min-h-[200px]">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <div className="text-red-600 text-center max-w-md mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col items-center justify-center min-h-[200px]">
          <div className="text-gray-400 text-4xl mb-4">📅</div>
          <div className="text-gray-600 text-center">No schedule data available</div>
        </div>
      </div>
    );
  }

  // Color palette for classes
  const classColors = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-yellow-100 text-yellow-800',
    'bg-pink-100 text-pink-800',
    'bg-purple-100 text-purple-800',  
    'bg-red-100 text-red-800',
    'bg-indigo-100 text-indigo-800',
    'bg-teal-100 text-teal-800',
    'bg-orange-100 text-orange-800',
    'bg-cyan-100 text-cyan-800',
  ];

  // Use allUniqueClasses from props for color mapping
  const uniqueClasses = allUniqueClasses || Array.from(new Set(data.map(row => row.class).filter(Boolean)));
  // Map class value to color
  const getClassColor = (classValue) => {
    const idx = uniqueClasses.indexOf(classValue);
    if (idx === -1) return 'bg-gray-100 text-gray-800';
    return classColors[idx % classColors.length];
  };

  const isConcernRow = (row) => Boolean(row.lateCount || row.routineTimeChange);

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="w-full min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[140px] max-w-[180px]">Class Date</th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                Time
              </th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                Class
              </th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[120px] max-w-[160px]">Subject</th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[80px] max-w-[120px]">Chapter</th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[40px] max-w-[60px]">Part</th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[100px] max-w-[140px]">Topic</th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[80px] max-w-[120px]">Teacher</th>
              <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 min-w-[100px] max-w-[140px]">Project Name</th>
              <th className="whitespace-nowrap px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200 w-12">Details</th>
            </tr>
          </thead>
          <tbody>
            {data.filter(row => row.teacher && row.teacher !== 'N/A' && row.date && row.date !== 'N/A').map((row, index) => {
              const concern = isConcernRow(row);
              return (
              <tr 
                key={row.id || index} 
                className={`transition-colors duration-150 cursor-pointer ${
                  concern
                    ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => handleRowClick(row)}
              >
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[140px] max-w-[180px] truncate overflow-ellipsis overflow-hidden ${concern ? 'text-red-900 font-medium' : 'text-gray-900'}`}>{formatDate(row.date)}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 ${concern ? 'text-red-900 font-medium' : 'text-gray-900'}`}>
                  {formatTime(row.time) || 'TBD'}
                </td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm font-semibold border-b border-gray-200 min-w-[80px] max-w-[120px] truncate text-center rounded ${getClassColor(row.class)}`}>
                  {row.class || 'N/A'}
                </td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[120px] max-w-[160px] truncate overflow-ellipsis overflow-hidden ${concern ? 'text-red-900' : 'text-gray-900'}`}>
                  {row.subject || 'N/A'}
                </td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[80px] max-w-[120px] truncate overflow-ellipsis overflow-hidden ${concern ? 'text-red-900' : 'text-gray-900'}`}>{row.chapter || 'N/A'}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[40px] max-w-[60px] truncate overflow-ellipsis overflow-hidden ${concern ? 'text-red-900' : 'text-gray-900'}`}>{row.part || 'N/A'}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[100px] max-w-[140px] truncate ${concern ? 'text-red-900' : 'text-gray-900'}`}>{row.topic || 'N/A'}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[80px] max-w-[120px] truncate ${concern ? 'text-red-900' : 'text-gray-900'}`}>{row.teacher || 'N/A'}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm border-b border-gray-200 min-w-[100px] max-w-[140px] truncate ${concern ? 'text-red-900' : 'text-gray-900'}`}>{row.projectName || 'N/A'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-center border-b border-gray-200 w-12">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(row);
                    }}
                    className={`p-1 rounded transition-colors duration-200 ${
                      concern
                        ? 'text-red-500 hover:text-red-700 hover:bg-red-100'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title="View Details"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && selectedRow && (() => {
        const concern = isConcernRow(selectedRow);
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            concern ? 'bg-red-50 ring-2 ring-red-400' : 'bg-white'
          }`}>
            <div className={`flex justify-between items-center p-6 border-b ${
              concern ? 'border-red-200 bg-red-100' : 'border-gray-200'
            }`}>
              <h2 className={`text-xl font-semibold ${concern ? 'text-red-900' : 'text-gray-900'}`}>
                Class Details
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {concern && (
                <div className="rounded-lg border border-red-300 bg-red-100 px-4 py-3 text-sm text-red-900">
                  <p className="font-semibold mb-1">Attention needed</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {selectedRow.lateCount && <li>Late Count</li>}
                    {selectedRow.routineTimeChange && <li>Routine Time Change</li>}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class Date</label>
                  <p className={concern ? 'text-red-900 font-medium' : 'text-gray-900'}>{formatDate(selectedRow.date)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <p className={concern ? 'text-red-900 font-medium' : 'text-gray-900'}>{formatTime(selectedRow.time) || 'TBD'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                  <p className="text-gray-900">{selectedRow.class || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <p className="text-gray-900">{selectedRow.subject || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chapter</label>
                  <p className="text-gray-900">{selectedRow.chapter || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Part</label>
                  <p className="text-gray-900">{selectedRow.part || 'N/A'}</p>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                  <p className="text-gray-900">{selectedRow.topic || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                  <p className="text-gray-900">{selectedRow.teacher || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                  <p className="text-gray-900">{selectedRow.projectName || 'N/A'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Late Count</label>
                  <p className={selectedRow.lateCount ? 'text-red-700 font-semibold' : 'text-gray-900'}>
                    {selectedRow.lateCount ? 'TRUE' : 'FALSE'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Routine Time Change</label>
                  <p className={selectedRow.routineTimeChange ? 'text-red-700 font-semibold' : 'text-gray-900'}>
                    {selectedRow.routineTimeChange ? 'TRUE' : 'FALSE'}
                  </p>
                </div>
              </div>
              
              {/* Thumbnail / Reminder Poster */}
              {(selectedRow.thumbnail || selectedRow.reminderPoster) && (() => {
                const showThumbnail = mediaTab === 'thumbnail' && selectedRow.thumbnail;
                const activeUrl = showThumbnail ? selectedRow.thumbnail : selectedRow.reminderPoster;
                const activeLabel = showThumbnail ? 'Thumbnail' : 'Reminder Poster';
                const hasBoth = Boolean(selectedRow.thumbnail && selectedRow.reminderPoster);

                return (
                  <div className={`pt-4 border-t ${concern ? 'border-red-200' : 'border-gray-200'}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      {hasBoth ? (
                        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group" aria-label="Media type">
                          <button
                            type="button"
                            onClick={() => setMediaTab('thumbnail')}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                              mediaTab === 'thumbnail'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Thumbnail
                          </button>
                          <button
                            type="button"
                            onClick={() => setMediaTab('reminderPoster')}
                            className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 transition-colors duration-200 ${
                              mediaTab === 'reminderPoster'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Reminder Poster
                          </button>
                        </div>
                      ) : (
                        <label className="block text-sm font-medium text-gray-700">{activeLabel}</label>
                      )}
                      {activeUrl && (
                        <a
                          href={activeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </a>
                      )}
                    </div>
                    {activeUrl ? (
                      <div className="flex justify-center">
                        <iframe
                          key={activeUrl}
                          src={convertGoogleDriveLink(activeUrl)}
                          className="w-full max-w-full h-96 rounded-lg shadow-md border border-gray-300"
                          title={activeLabel}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No {activeLabel.toLowerCase()} available.</p>
                    )}
                  </div>
                );
              })()}
              
              {/* Show raw data for debugging */}
              <div className={`mt-6 pt-4 border-t ${concern ? 'border-red-200' : 'border-gray-200'}`}>
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    Raw Data (for debugging)
                  </summary>
                  <pre className="mt-2 text-xs text-gray-600 bg-white/70 p-3 rounded overflow-x-auto">
                    {JSON.stringify(selectedRow, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
            
            <div className={`flex justify-end p-6 border-t ${concern ? 'border-red-200' : 'border-gray-200'}`}>
              <button
                onClick={closeModal}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
} 